import type { Restaurant } from '@/features/restaurants/types';
import type { PrintOrderPayload } from '@/shared/types/electron';

/**
 * Builds the restaurant header fields of a print payload from the
 * `Restaurant` domain object the auth/restaurant store already has loaded.
 * Used by the Test Print buttons today and by the live order pipeline (F-7)
 * tomorrow — single source of truth for "what does our masthead look like".
 */
export function restaurantHeaderFields(
  restaurant: Restaurant | null | undefined,
): Pick<
  PrintOrderPayload,
  | 'restaurantName'
  | 'restaurantPhone'
  | 'restaurantEmail'
  | 'restaurantAddress'
  | 'restaurantVatId'
  | 'currency'
> {
  if (!restaurant) {
    return {
      restaurantName: 'Restaurant OS',
    };
  }
  const street = [restaurant.street, restaurant.houseNumber].filter(Boolean).join(' ');
  const cityLine = [restaurant.postalCode, restaurant.city].filter(Boolean).join(' ');
  const addressLine = [street, cityLine, restaurant.country].filter(Boolean).join(', ');
  return {
    restaurantName: restaurant.name,
    restaurantPhone: restaurant.contactPhone || undefined,
    restaurantEmail: restaurant.contactEmail || undefined,
    restaurantAddress: addressLine || undefined,
    restaurantVatId: restaurant.taxNumber || undefined,
    currency: restaurant.currency || undefined,
  };
}

/**
 * A self-contained sample order used by the "Test print" buttons. The
 * customer + items are fixed (so the user can recognize the test receipt at
 * a glance); the restaurant header comes from `currentRestaurant` so what
 * prints matches what the real receipt will show.
 */
export function buildSampleOrder(
  restaurant: Restaurant | null | undefined,
  now = new Date(),
): PrintOrderPayload {
  const header = restaurantHeaderFields(restaurant);
  const currency = header.currency ?? 'EUR';
  return {
    orderId: 'o_TEST_8a3f2b91c4d6',
    orderShortId: 'TEST',
    orderType: 'DELIVERY',
    paymentType: 'Card · Stripe',
    paymentStatus: 'PAID',
    customer: {
      name: 'Anna Schmidt',
      phone: '+49 170 1234567',
      email: 'anna.schmidt@example.com',
    },
    items: [
      {
        name: 'Margherita Pizza',
        variantName: 'Large',
        quantity: 2,
        unitPrice: 9.0,
        itemNotes: 'Well done please, no garlic',
        addOns: [
          { name: 'Extra cheese', price: 1.5 },
          { name: 'Olives', price: 0.8 },
        ],
      },
      {
        name: 'Diavola Pizza',
        variantName: 'Medium',
        quantity: 1,
        unitPrice: 11.5,
        itemNotes: 'Extra spicy',
      },
      { name: 'Coke', variantName: '0.5L', quantity: 2, unitPrice: 3.5 },
      { name: 'Tiramisu', quantity: 1, unitPrice: 4.5 },
    ],
    subtotal: 38.3,
    deliveryFee: 3.5,
    tax: 0,
    discountAmount: 3.83,
    discountName: 'WELCOME10',
    total: 37.97,
    currency,
    deliveryAddress: {
      street: 'Hauptstraße',
      houseNumber: '12',
      postalCode: '10115',
      city: 'Berlin',
      country: 'Germany',
      deliveryInstructions: 'Ring twice, 3rd floor, name SCHMIDT on the buzzer',
    },
    orderNotes: 'Please leave at door if no one answers.',
    createdAt: now.toISOString(),
    estimatedReadyTime: new Date(now.getTime() + 30 * 60 * 1000).toISOString(),
    ...header,
  };
}
